import type { ReactNode } from "react";
import { Button, Card, Skeleton } from "@heroui/react";

export function SkeletonBlock({ className = "" }: { className?: string }) {
  return <Skeleton className={`rounded-lg ${className}`} />;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Card>
      <Card.Content className="flex flex-col items-center gap-3 p-8 text-center">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
        {description ? <p className="text-sm text-muted">{description}</p> : null}
        {action}
      </Card.Content>
    </Card>
  );
}

export function SkeletonDashboard() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBlock key={`kpi-${i}`} className="h-24" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <SkeletonBlock className="h-64" />
        <SkeletonBlock className="h-64" />
      </div>
      <SkeletonBlock className="h-80" />
    </div>
  );
}

export function SkeletonTable() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonBlock key={`row-${i}`} className="h-12" />
      ))}
    </div>
  );
}

export function SkeletonDetail() {
  return (
    <div className="flex flex-col gap-4">
      <SkeletonBlock className="h-10 w-1/3" />
      <SkeletonBlock className="h-48" />
      <SkeletonBlock className="h-24" />
    </div>
  );
}

export function SkeletonForm() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonBlock key={`field-${i}`} className="h-14" />
      ))}
      <SkeletonBlock className="h-10 w-32" />
    </div>
  );
}

export function SkeletonStorefront() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonBlock key={`card-${i}`} className="h-64" />
      ))}
    </div>
  );
}

export function ErrorState({
  title = "Terjadi kesalahan",
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <Card role="alert">
      <Card.Content className="flex flex-col items-center gap-3 p-8 text-center">
        <h3 className="text-lg font-semibold text-danger">{title}</h3>
        {description ? <p className="text-sm text-muted">{description}</p> : null}
        {onRetry ? (
          <Button variant="primary" onPress={onRetry}>
            Coba lagi
          </Button>
        ) : null}
      </Card.Content>
    </Card>
  );
}

const ERROR_PAGE: Record<
  401 | 403 | 404 | 500 | 503,
  { title: string; description: string; cta: string }
> = {
  401: {
    title: "Sesi berakhir",
    description: "Masuk kembali untuk melanjutkan.",
    cta: "Login ulang",
  },
  403: {
    title: "Tidak punya akses",
    description: "Akun ini tidak dapat membuka halaman tersebut.",
    cta: "Kembali",
  },
  404: {
    title: "Halaman tidak ditemukan",
    description: "Periksa tautan atau kembali ke beranda permukaan ini.",
    cta: "Kembali ke beranda",
  },
  500: {
    title: "Terjadi kesalahan",
    description: "Coba lagi. Jika berlanjut, laporkan kode jejak di bawah.",
    cta: "Coba lagi",
  },
  503: {
    title: "Pasar sedang dirawat",
    description: "Katalog akan kembali setelah perawatan selesai.",
    cta: "Kembali ke katalog",
  },
};

export function ErrorPage({
  code = 500,
  title,
  description,
  traceId,
  onAction,
  actionHref,
}: {
  code?: 401 | 403 | 404 | 500 | 503;
  title?: string;
  description?: string;
  traceId?: string;
  onAction?: () => void;
  actionHref?: string;
}) {
  const copy = ERROR_PAGE[code];
  const heading = title ?? copy.title;
  const body = description ?? copy.description;

  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <p className="text-sm tabular-nums text-muted">{code}</p>
      <h1 className="text-2xl font-semibold">{heading}</h1>
      <p className="max-w-prose text-sm text-muted">{body}</p>
      {code === 500 && traceId ? (
        <p className="font-mono text-xs text-muted">
          trace_id: <span className="select-all">{traceId}</span>
        </p>
      ) : null}
      {onAction || actionHref ? (
        <Button
          variant="primary"
          onPress={() => {
            if (onAction) {
              onAction();
              return;
            }
            if (actionHref) window.location.assign(actionHref);
          }}
        >
          {copy.cta}
        </Button>
      ) : null}
    </main>
  );
}
